/**
 * Vega-Like tools add support for transformation, parameters, and value selection in the dashboard schemas.
 * It's not Vega, it is Vega-Like
 */

import { codegenExpression, parseExpression, constants as vegaConstants, functions as vegaFunctions } from "vega-expression";
import { Value, VegaLikeSchemaBase, VegaLikeSchemaRoot } from "./VegaLikeToolsTypes";

import { parseVegaLikeBindings } from "./VegaLikeToolsBind";






export function vegaLikeGetInfo(obj: any) {

    let   sch = obj as VegaLikeSchemaRoot;
    const isSchema = typeof sch === "object" && sch != null && typeof sch.$schema === "string";

    const debug = !!(isSchema && sch.debug);
    return { isSchema, debug };
}


/**
 * 
 * @param obj 
 * @returns 
 */

export function vegaLikeGetSchema<T>(obj: any, memory: any = {}): T {

    let   schema = obj as T;
    let   sch = obj as VegaLikeSchemaRoot;
    const isSchema = typeof sch === "object" && sch != null && typeof sch.$schema === "string";

    if (!isSchema) {
        return null;
    }

    if (!memory.__global__) {
        memory.__global__ = {};
    }
    if (!memory.__bind__) {
        memory.__bind__ = {};
    }
    const __globobj__ = memory.__global__;
    const __bind__ = memory.__bind__;


    if (Array.isArray(sch.params)) {
        for (const param of sch.params) {
            if ("value" in param && param.value !== undefined) {
                __globobj__[param.name] = __bind__.hasOwnProperty(param.name) ? __bind__[param.name] : param.value;
            }
        }
        for (const param of sch.params) {
            if ("expr" in param && param.expr) {
                const fn = getFn(param.expr, __globobj__);
                __globobj__[param.name] = __bind__.hasOwnProperty(param.name) ? __bind__[param.name] : fn(sch.data?.values?.[0]);
            }
        }
    }

    let values = vegaLikeTransforms(sch.data?.values || [], sch.transform, __globobj__);
    schema = { ...schema, data: { ...sch.data, values }, __globobj__};
   
    return schema;
}



interface VegaLikeLayer {
    mark: string;
    encoding: any;
    options: any;
    data: {
        values: any[];
    };
    enable?: string;
}


/**
 * Process all layers of a Vega-Like schema. The function will call the layerFunction for each layer.
 * If the sequence of the layer types (marks) is important, then the supportedMarks parameter should be used to filter the layers.
 * 
 * @param obj 
 * @param supportedMarks array of supported marks.
 * @param layerFunction 
 */

export function parseVegaLikeSchema(
    obj: any, 
    supportedMarks: string[],
    memory: any, needUpdate: () => void,
    layerFunction: (
        layer: VegaLikeLayer, 
        readValue: <T>(field: Value<T>, type: string, idx?: number) => T,
        readValueFn: <T>(field: Value<T>, type: string) => ((row: any) => T)) => void
) {

    const rootSchema: VegaLikeSchemaRoot = vegaLikeGetSchema({ ...obj }, memory);

    for (const mark of supportedMarks || [null]) {
        for (const layer of [rootSchema, ...(rootSchema.layer || [])]) {

            if (!rootSchema.encoding && !layer.encoding) { continue; }
            if (layer.enable) {
                const fn = getFn(layer.enable, rootSchema.__globobj__);
                if (!fn(rootSchema.data?.values?.[0])) { continue; }
            }

            let { type, ...options } = typeof layer.mark === "string" ? { type: layer.mark } : 
                            typeof layer.mark === "object" && layer.mark?.type ? layer.mark :
                                { type: "" };

            if (!type) { continue; }
            if (mark && type !== mark) { continue; }

            const encoding = { ...rootSchema.encoding, ...layer.encoding };
            const values = layer === rootSchema ? layer.data?.values : vegaLikeTransforms(layer.data?.values || rootSchema.data?.values, layer.transform, rootSchema.__globobj__);

            options = { ...rootSchema.config?.[type], ...options };

            layerFunction({ mark: type, encoding, options, data: { ...layer.data, values } },
                <T>(field: Value<T>, type: any, idx = null) => {
                    return readValue({ ...layer, data: { ...rootSchema.data, values }, __globobj__: rootSchema.__globobj__}, field as any, type, idx) as T;  // FIXME: no any please
                },
                <T>(field: Value<T>, type: any, idx = null) => {
                    const v = readValue({ ...layer, data: { ...rootSchema.data, values }, __globobj__: rootSchema.__globobj__}, field as any, type, idx);
                    return typeof v === "function" || v == null ? v : () => v
                }
            );
        }
    }


    return parseVegaLikeBindings(rootSchema, memory, needUpdate);
}




/**
 * Read a value. The value can be either:
 *    a constant value direct (not an object)
 *    field - name/key of field in data.values[idx][key] to be read. Field values are subject to scale
 *    datum - constant value. The value is subject to scale.
 *    value - constant value. Not subject to scale, but returned directly as is
 *    expr  - javascript like expression. not subject to scale
 * 
 * @param obj 
 * @param field 
 * @param type 
 * @returns 
 */
export function readValue(obj: VegaLikeSchemaBase, field: Value, type: "string" | "number" | "boolean" | "any", idx = null) {

	if (field == null) { return null; }
	if (typeof field === "string" || typeof field === "number" || typeof field === "boolean") {
		return type === typeof field || type === "any" ? field : null;
	}
	if (typeof field === "object") {
        let scale = null;

        if ("scale" in field && Array.isArray(field.scale?.domain) && Array.isArray(field.scale?.range)) {
            scale = (x) => { let i = field.scale.domain.indexOf(x); return i < 0 ? null : field.scale.range[i]; }
        }

        if ("value" in field && field.value != null) { return type === "any" || typeof field.value === type ? field.value : null; }
		if ("datum" in field && field.datum != null) { return type === "any" || typeof field.datum === type ? (scale ? scale(field.datum) : field.datum) : null; }
		if ("field" in field && field.field) { 
            return idx != null 
                ? (scale ? scale(obj.data?.values?.[idx]?.[field.field]) : obj.data?.values?.[idx]?.[field.field])
                : (scale ? ((o) => scale(o?.[field.field])) : ((o) => o?.[field.field]));
        }
		if ("expr" in field && field.expr) { 
            const fn = getFn(field.expr, obj.__globobj__);
            return idx != null ? fn(obj.data.values?.[idx]) : fn;
        }
	}

	return null;
}


interface VegaLikeOpObj {
    op: string;
    values: any[];
}

/**
 * eval pivot and aggregation data
 * @param op 
 * @returns 
 */
function evalOp(op: VegaLikeOpObj) {

    if (op.op === "sum") {
        return op.values.reduce((sum, c) => sum + c, 0);
    } else if (op.op === "mean" || op.op === "average") {
        return op.values.length > 0 ? op.values.reduce((sum, c) => sum + c, 0) / op.values.length : NaN;
    } else if (op.op === "count") {
        return op.values.length;
    } else if (op.op === "missing") {
        return op.values.filter(v => v == null).length;
    } else if (op.op === "valid") {
        return op.values.filter(v => v != null && Number.isNaN(v) === false).length;
    } else if (op.op === "distinct") {
        return Object.keys(op.values.reduce((set, c) => ({ ...set, [c]: 1 }), {})).length;
    } else if (op.op === "min") {
        return op.values.reduce((min, c) => Math.min(c, min ?? c));
    } else if (op.op === "max") {
        return op.values.reduce((max, c) => Math.max(c, max ?? c));
    } else if (op.op === "values") {
        return op.values;
    } else if (op.op === "median") {
        const sorted = op.values.slice().sort((a, b) => a - b);
        const middle = Math.floor(sorted.length / 2);
        return (sorted.length % 2 === 0 && sorted.length > 1) ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
    }
    throw new Error("Unsupported operator " + op.op);
}



const getFn = (code: string, __globobj__): ((datum: any) => any) => {
    const ast = parseExpression(code);
    const fn = codegenExpression({
        fieldvar: "datum",
        constants: vegaConstants,
        functions: vegaFunctions,
        forbidden: ["_", "window", "document"],
        allowed: ["datum"],
        globalvar: "__globobj__",
    })(ast).code;

    const ev = `(datum) => (${fn})`;
    return eval(ev);
}

/**
 * Take a values array and a transformations array and return a new values array that has been transformed
 * according to the list of transformations. The supported transformations are a subset of the vega-lite
 * supported transformations.
 * 
 * @param values 
 * @param transforms 
 * @returns 
 */
export function vegaLikeTransforms(values: any[], transforms: VegaLikeSchemaBase["transform"], __globobj__: any) {

    if (!transforms || Array.isArray(transforms) === false || transforms.length === 0) { return values; }

	values = [...values];



	for (const trans of transforms) {
        // Clean way: check for unique keys that identify each transform type
        if ("calculate" in trans && typeof trans.calculate === "string" && typeof trans.as === "string") {
            // Calculate transform
            const as = trans.as;
            const fn = getFn(trans.calculate, __globobj__);

            for (let i = 0; i < values.length; i++) {
            values[i] = { ...values[i], [as]: fn(values[i]) };
            }

        } else if ("filter" in trans && typeof trans.filter === "string") {
            // Filter transform
            const fn = getFn(trans.filter, __globobj__);
            values = values.filter(datum => fn(datum));

        } else if ("pivot" in trans && typeof trans.pivot === "string" && typeof trans.value === "string") {
            // Pivot transform
            const doOpOnObj = (datum: {[key: string]: number | string | VegaLikeOpObj}) => {
            for (const key of Object.keys(datum)) {
                if (typeof datum[key] === "object" && datum[key].op) {
                datum[key] = evalOp(datum[key]);
                }
            }
            return datum;
            };

            if (Array.isArray(trans.groupby) && trans.groupby.length > 0) {
            let sets: {
                [groupby: string]: {
                [key: string]: number | string | VegaLikeOpObj;
                }
            } = {};

            for (const datum of values) {
                const key = datum[trans.pivot];
                const groupby = trans.groupby.map(k => datum[k]).join(":");

                let res = sets[groupby];

                if (!res) {
                res = sets[groupby] = trans.groupby.reduce((res, k) => ({ ...res, [k]: datum[k] }), {});
                }
                if (!res[key]) { res[key] = { op: trans.op || "sum", values: [] }; }
                (res[key] as VegaLikeOpObj).values.push(datum[trans.value]);
            }
            values = [];
            for (const setKey of Object.keys(sets)) {
                values.push(doOpOnObj(sets[setKey]));
            }

            } else {
            // No pivot needed
            let res = {};
            for (const datum of values) {
                const key = datum[trans.pivot];
                if (!res[key]) { res[key] = { op: trans.op || "sum", values: [] }; }
                (res[key] as VegaLikeOpObj).values.push(datum[trans.value]);
            }
            values = [doOpOnObj(res)];
            }
        }

	}

	return values;

}

